import type { TFunction } from 'i18next';

import type { HealthOverviewParameter } from '@/domain/health';

import { getHealthAlertDisplayLevel, type HealthAlertDisplayLevel } from './healthAlertDisplay';

/** User-facing status tier (presentation only; maps from existing alert rules). */
export type HealthParameterStatusLevel = 'normal' | 'elevated' | 'significantlyElevated';

/** User-facing development tier (presentation only; maps from slope + alert). */
export type HealthParameterDevelopmentLevel =
    | 'stable'
    | 'decreasing'
    | 'increasing'
    | 'rapidlyIncreasing';

const RAPID_SLOPE_MULTIPLIER = 1.25;

const STATUS_SORT_ORDER: Record<HealthParameterStatusLevel, number> = {
    significantlyElevated: 0,
    elevated: 1,
    normal: 2,
};

const DISPLAY_LEVEL_SORT_ORDER: Record<HealthAlertDisplayLevel, number> = {
    high: 0,
    medium: 1,
    low: 2,
};

export function getHealthParameterStatusLevel(
    parameter: Pick<HealthOverviewParameter, 'alert'>
): HealthParameterStatusLevel {
    if (parameter.alert.level === 'high') {
        return 'significantlyElevated';
    }
    if (parameter.alert.level === 'increasing') {
        return 'elevated';
    }
    return 'normal';
}

export function getHealthParameterDevelopmentLevel(
    parameter: Pick<HealthOverviewParameter, 'alert' | 'daily' | 'weekly'>
): HealthParameterDevelopmentLevel {
    const slope = parameter.daily?.slope ?? parameter.weekly?.slope ?? null;

    // API stable — no flagged alert; always flat development (matches detail panel before Status/Development split).
    if (parameter.alert.level === 'stable') {
        return 'stable';
    }

    // No trend or flat slope on a flagged alert — Stable, not Increasing.
    if (slope == null || slope === 0) {
        return 'stable';
    }

    if (slope < 0) {
        return 'decreasing';
    }

    if (parameter.alert.level === 'increasing') {
        const threshold = parameter.alert.appliedThreshold;
        if (threshold != null && slope >= threshold * RAPID_SLOPE_MULTIPLIER) {
            return 'rapidlyIncreasing';
        }
        return 'increasing';
    }

    if (parameter.alert.level === 'high') {
        return 'increasing';
    }

    return 'stable';
}

export function getHealthParameterStatusDisplayLevel(
    parameter: Pick<HealthOverviewParameter, 'alert' | 'daily'>
): HealthAlertDisplayLevel | null {
    const status = getHealthParameterStatusLevel(parameter);
    if (status === 'normal') {
        return null;
    }
    if (status === 'significantlyElevated') {
        return 'high';
    }
    return getHealthAlertDisplayLevel(parameter);
}

export function getHealthParameterStatusLabel(
    status: HealthParameterStatusLevel,
    t: TFunction<'healthModule'>
): string {
    switch (status) {
        case 'significantlyElevated':
            return t('dashboard.statusSignificantlyElevated');
        case 'elevated':
            return t('dashboard.statusElevated');
        case 'normal':
            return t('dashboard.statusNormal');
    }
}

export function getHealthParameterStatusTooltip(
    status: HealthParameterStatusLevel,
    t: TFunction<'healthModule'>
): string | null {
    switch (status) {
        case 'significantlyElevated':
            return t('dashboard.statusSignificantlyElevatedTooltip');
        case 'elevated':
            return t('dashboard.statusElevatedTooltip');
        case 'normal':
            return null;
    }
}

export function getHealthParameterDevelopmentLabel(
    development: HealthParameterDevelopmentLevel,
    t: TFunction<'healthModule'>
): string {
    switch (development) {
        case 'rapidlyIncreasing':
            return t('dashboard.developmentRapidlyIncreasing');
        case 'increasing':
            return t('dashboard.developmentIncreasing');
        case 'decreasing':
            return t('dashboard.developmentDecreasing');
        case 'stable':
            return t('dashboard.developmentStable');
    }
}

export function getHealthParameterDevelopmentTooltip(
    development: HealthParameterDevelopmentLevel,
    t: TFunction<'healthModule'>
): string | null {
    switch (development) {
        case 'rapidlyIncreasing':
            return t('dashboard.developmentRapidlyIncreasingTooltip');
        case 'increasing':
            return t('dashboard.developmentIncreasingTooltip');
        case 'decreasing':
        case 'stable':
            return null;
    }
}

/** Varsler sort: status severity, then display tier within elevated, then API priority. */
export function compareHealthAlertParameters(
    a: HealthOverviewParameter,
    b: HealthOverviewParameter
): number {
    const statusDiff =
        STATUS_SORT_ORDER[getHealthParameterStatusLevel(a)] -
        STATUS_SORT_ORDER[getHealthParameterStatusLevel(b)];

    if (statusDiff !== 0) {
        return statusDiff;
    }

    const displayDiff =
        DISPLAY_LEVEL_SORT_ORDER[getHealthAlertDisplayLevel(a)] -
        DISPLAY_LEVEL_SORT_ORDER[getHealthAlertDisplayLevel(b)];

    if (displayDiff !== 0) {
        return displayDiff;
    }

    return b.alert.priority - a.alert.priority;
}
